import {
  PatientAuthorizationAllowanceBasis,
} from 'dtos/patientAuthorizationAllowanceBasis';
import {
  PatientAuthorizationUtilizationState,
} from 'dtos/patientAuthorizationUtilizationState';
import type {
  PatientAuthorizationAllowanceSummaryDTO,
  PatientAuthorizationListItemDTO,
  PatientAuthorizationResponseDTO,
} from 'dtos';

export type AuthorizationUnitSubrow = {
  code: string;
  description: string;
  approvedQuantity: number;
  usedQuantity: number;
  allowanceId: number;
  allowanceIndex: number;
};

export type AuthorizationRow = PatientAuthorizationListItemDTO & {
  authorizationUnitSubrow?: AuthorizationUnitSubrow;
};

function getUnitAllowances(row: AuthorizationRow) {
  return row.allowances.filter(
    (allowance) => allowance.allowanceBasis === PatientAuthorizationAllowanceBasis.Units,
  );
}

export function canExpandAuthorization(row: AuthorizationRow): boolean {
  return row.authorizationUnitSubrow === undefined
    && Array.isArray(row.allowances)
    && getUnitAllowances(row)
    .some((allowance) => allowance.codes.length > 0);
}

export function getAuthorizationUnitSubrows(row: AuthorizationRow): AuthorizationRow[] {
  if (!canExpandAuthorization(row)) {
    return [];
  }

  return getUnitAllowances(row).flatMap((allowance, allowanceIndex) => (
    allowance.codes.map(({ code, description }) => ({
      ...row,
      authorizationUnitSubrow: {
        code,
        description,
        approvedQuantity: allowance.approvedQuantity,
        usedQuantity: allowance.usedQuantity + allowance.externalUsedQuantity,
        allowanceId: allowance.patientAuthorizationAllowanceId,
        allowanceIndex,
      },
    }))
  ));
}

type AuthorizationQuantityTotals = {
  allowanceBasis: PatientAuthorizationAllowanceBasis;
  approvedQuantity: number;
  usedQuantity: number;
};

export function getAuthorizationQuantityTotals(
  allowances: AuthorizationRow['allowances'],
): AuthorizationQuantityTotals[] {
  const totals = new Map<PatientAuthorizationAllowanceBasis, AuthorizationQuantityTotals>();

  allowances.forEach((allowance) => {
    const total = totals.get(allowance.allowanceBasis) ?? {
      allowanceBasis: allowance.allowanceBasis,
      approvedQuantity: 0,
      usedQuantity: 0,
    };
    total.approvedQuantity += allowance.approvedQuantity;
    total.usedQuantity += allowance.usedQuantity + allowance.externalUsedQuantity;
    totals.set(allowance.allowanceBasis, total);
  });

  return Array.from(totals.values());
}

export type ClaimAuthorizationOption = {
  authorizationNumber: string;
  status: PatientAuthorizationUtilizationState | undefined;
  authorizations: Pick<
    PatientAuthorizationListItemDTO,
    | 'patientAuthorizationId'
    | 'effectiveDate'
    | 'expirationDate'
    | 'notes'
    | 'allowances'
    | 'status'
    | 'updatedOn'
  >[];
};

export function getAuthorizationStatus(
  allowances: readonly PatientAuthorizationAllowanceSummaryDTO[],
): PatientAuthorizationUtilizationState | undefined {
  const statuses = allowances.map((allowance) => allowance.status);
  const hasAvailable = statuses.includes(PatientAuthorizationUtilizationState.Available);
  const hasExhausted = statuses.includes(PatientAuthorizationUtilizationState.Exhausted);

  if (hasAvailable) {
    return hasExhausted
      ? PatientAuthorizationUtilizationState.PartlyExhausted
      : PatientAuthorizationUtilizationState.Available;
  }
  if (hasExhausted) {
    return PatientAuthorizationUtilizationState.Exhausted;
  }
  if (statuses.includes(PatientAuthorizationUtilizationState.Upcoming)) {
    return PatientAuthorizationUtilizationState.Upcoming;
  }
  return statuses[0];
}

export function getAuthorizationStatusLabel(status: PatientAuthorizationUtilizationState): string {
  return status === PatientAuthorizationUtilizationState.PartlyExhausted ? 'Partly exhausted' : status;
}

export function getClaimAuthorizationOptionModels(
  authorizations: (PatientAuthorizationListItemDTO | PatientAuthorizationResponseDTO)[],
): ClaimAuthorizationOption[] {
  const options = new Map<string, ClaimAuthorizationOption>();
  authorizations.forEach((authorization) => {
    const option = options.get(authorization.authorizationNumber) ?? {
      authorizationNumber: authorization.authorizationNumber,
      status: undefined,
      authorizations: [],
    };
    option.authorizations.push(authorization);
    options.set(option.authorizationNumber, option);
  });

  return Array.from(options.values(), (option) => ({
    ...option,
    status: getAuthorizationStatus(option.authorizations.flatMap((authorization) => authorization.allowances)),
  }));
}

function allowanceMatchesCodes(
  allowance: PatientAuthorizationAllowanceSummaryDTO,
  codes: readonly string[],
): boolean {
  return (allowance.allowanceBasis === PatientAuthorizationAllowanceBasis.Visits && allowance.codes.length === 0)
    || allowance.codes.some(({ code }) => codes.includes(code));
}

function sortClaimAuthorizationOptions(
  options: ClaimAuthorizationOption[],
): ClaimAuthorizationOption[] {
  function getSortOrder(option: ClaimAuthorizationOption): number {
    switch (option.status) {
      case PatientAuthorizationUtilizationState.Available:
        return 0;
      case PatientAuthorizationUtilizationState.PartlyExhausted:
        return 1;
      case PatientAuthorizationUtilizationState.Upcoming:
        return 2;
      case PatientAuthorizationUtilizationState.Exhausted:
        return 3;
      case PatientAuthorizationUtilizationState.Expired:
        return 4;
      default:
        return Infinity;
    }
  }

  function hasVisits(option: ClaimAuthorizationOption): boolean {
    return option.authorizations.some(
      (authorization) => authorization.allowances.some(
        (allowance) => allowance.allowanceBasis === PatientAuthorizationAllowanceBasis.Visits,
      ),
    );
  }

  return [...options].sort((left, right) => getSortOrder(left) - getSortOrder(right)
    || Number(hasVisits(right)) - Number(hasVisits(left)));
}

export function getClaimAuthorizationOptions(
  authorizations: (PatientAuthorizationListItemDTO | PatientAuthorizationResponseDTO)[],
  claimCodes: readonly string[],
  currentAuthorizationNumber?: string | null,
): ClaimAuthorizationOption[] {
  return sortClaimAuthorizationOptions(
    getClaimAuthorizationOptionModels(authorizations).filter((option) => {
      const isCurrent = option.authorizationNumber === currentAuthorizationNumber;
      return option.authorizations.some((authorization) => (
        authorization.allowances.some((allowance) => (
          (isCurrent || allowance.status !== PatientAuthorizationUtilizationState.Expired)
          && allowanceMatchesCodes(allowance, claimCodes)
        ))
      ));
    }).map((option) => ({
      ...option,
      status: getAuthorizationStatus(option.authorizations.flatMap((authorization) => (
        authorization.allowances.filter((allowance) => allowanceMatchesCodes(allowance, claimCodes))
      ))),
    })),
  );
}

export function getClaimAuthorizationOptionsForCode(
  options: ClaimAuthorizationOption[],
  currentAuthorizationNumber?: string | null,
  code?: string | null,
): ClaimAuthorizationOption[] {
  const codes = code ? [code] : [];
  const matchingOptions = options.map((option) => ({
    ...option,
    authorizations: option.authorizations.map((authorization) => {
      const allowances = authorization.allowances.filter((allowance) => allowanceMatchesCodes(allowance, codes));
      const status = getAuthorizationStatus(allowances) ?? authorization.status;

      // Keep the record notes when no allowance matches the code.
      return { ...authorization, allowances, status };
    }),
  }));

  return sortClaimAuthorizationOptions(matchingOptions.filter((option) => {
    const isCurrent = option.authorizationNumber === currentAuthorizationNumber;
    return isCurrent || option.authorizations.some((authorization) => (
      authorization.allowances.some((allowance) => allowance.status !== PatientAuthorizationUtilizationState.Expired)
    ));
  }).map((option) => ({
    ...option,
    status: getAuthorizationStatus(option.authorizations.flatMap((authorization) => authorization.allowances)),
  })));
}
